import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { FetchApi } from "../Api/AuthenticatedApi";

// Optional parts of the app an organization turns on under Settings. Each is
// a key in the org's settings map, "true" when on; missing means off. Turning
// one off only hides it: its data is kept and comes back when it's turned on.
export const FEATURES = {
  companies: "enable_contact_companies",
  workflows: "enable_workflows",
};

// featuresFrom reads the org's settings map into { companies, workflows }.
export function featuresFrom(settings) {
  const out = {};
  for (const [name, key] of Object.entries(FEATURES)) {
    out[name] = !!settings && settings[key] === "true";
  }
  return out;
}

// One fetch of the settings, shared by every screen that asks. Switching org
// reloads the page, so this never goes stale across orgs.
let cached = null;
let fetching = false;
const listeners = new Set();

function publish(settings) {
  cached = settings || {};
  listeners.forEach((fn) => fn(cached));
}

// setOrgSettings hands freshly saved settings to every screen using
// useOrgFeatures, so the sidebar updates without a reload.
export function setOrgSettings(settings) {
  publish(settings);
}

// useOrgFeatures returns { loaded, companies, workflows }. Until the settings
// load, every feature reads as off, so hidden things don't flash on screen.
export default function useOrgFeatures() {
  const { getAccessTokenSilently } = useAuth0();
  const [settings, setSettings] = React.useState(cached);

  React.useEffect(() => {
    listeners.add(setSettings);
    if (cached) {
      setSettings(cached);
    } else if (!fetching) {
      fetching = true;
      const done = () => { fetching = false; };
      FetchApi(getAccessTokenSilently, "/api/settings/organization",
        (data) => { done(); publish(data && data.settings); },
        done, done);
    }
    return () => { listeners.delete(setSettings); };
  }, [getAccessTokenSilently]);

  return { loaded: settings !== null, ...featuresFrom(settings) };
}
